@if (isLoading) {
  <div class="inventory-item-tab__loader">
    <div class="spinner-border" role="status">
      <span class="visually-hidden">{{ 'MESSAGES.LOADING' | translate }}</span>
    </div>
  </div>
} @else {
  <form [formGroup]="form" class="inventory-item-uom-and-prices">
    @let formInteracted = form.dirty || form.touched;
    @if (availableUnits$ | async; as availableUnits) {
      <div class="inventory-item-uom-and-prices__toolbar">
        @if (isEditMode) {
          <div class="inventory-item-uom-and-prices__add-controls">
            <div class="inventory-item-uom-and-prices__field">
              <label class="form-label" for="inventory-item-uom-and-prices-select">
                {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_AND_PRICES.ADD_LABEL' | translate }}
              </label>
              <select
                id="inventory-item-uom-and-prices-select"
                class="form-input form-input--compact inventory-item-uom-and-prices__select"
                formControlName="selectedUnitOfMeasureId"
              >
                <option [ngValue]="null">
                  {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_AND_PRICES.SELECT_PLACEHOLDER' | translate }}
                </option>
                @for (unit of availableUnits; track unit.id) {
                  <option [ngValue]="unit.id">{{ unit.displayName }}</option>
                }
              </select>
            </div>

            <div class="inventory-item-uom-and-prices__field inventory-item-uom-and-prices__field--actions">
              <button
                type="button"
                class="form-secondary-btn form-secondary-btn--small inventory-item-uom-and-prices__action-button"
                [disabled]="!canAddUnit"
                (click)="onAddUnit()"
              >
                <svg class="inventory-item-uom-and-prices__action-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M8 3.333v9.334M3.333 8h9.334" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                </svg>
                {{ 'BUTTONS.ADD' | translate }}
              </button>
            </div>
          </div>
        } @else {
          <div class="inventory-item-uom-and-prices__toolbar-spacer"></div>
        }

        <div class="inventory-item-uom-and-prices__action-group">
          @if (isEditMode) {
            <button
              type="button"
              class="form-secondary-btn form-secondary-btn--small inventory-item-uom-and-prices__action-button"
              [disabled]="!canCancelEdit"
              (click)="onCancelEdit()"
            >
              <svg class="inventory-item-uom-and-prices__action-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              </svg>
              {{ 'BUTTONS.CANCEL' | translate }}
            </button>
            <button
              type="button"
              class="form-primary-btn form-primary-btn--small inventory-item-uom-and-prices__action-button"
              [disabled]="!canSave"
              (click)="onSave()"
            >
              <svg class="inventory-item-uom-and-prices__action-icon" viewBox="0 0 16 12" fill="none" aria-hidden="true">
                <path d="M2 6.5 5.273 10 14 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ 'BUTTONS.SAVE_CHANGES' | translate }}
            </button>
          } @else {
            <button
              type="button"
              class="form-primary-btn form-primary-btn--small inventory-item-uom-and-prices__action-button"
              [disabled]="!canEnterEditMode"
              (click)="onStartEdit()"
            >
              <svg class="inventory-item-uom-and-prices__action-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="m11.333 2.333 2.334 2.334M10 3.667 4 9.667v2.666h2.667l6-6L10 3.667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              {{ 'BUTTONS.EDIT' | translate }}
            </button>
          }
        </div>
      </div>

      @if (isEditMode && availableUnits.length === 0) {
        <p class="inventory-item-uom-and-prices__hint">
          {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_AND_PRICES.ALL_UNITS_ADDED' | translate }}
        </p>
      }
    }

    @if (rowViewModels$ | async; as rows) {
      @if (rows.length) {
        <div class="inventory-item-tab__table-wrapper inventory-item-uom-and-prices__desktop-table">
          <div class="inventory-item-uom-and-prices__table-shell" formArrayName="rows">
            <table class="inventory-item-tab__table inventory-item-uom-and-prices__table">
              <thead>
                <tr>
                  <th>{{ 'COMPONENTS.GOODS.FIELDS.UNIT' | translate }}</th>
                  <th><span class="form-label required">{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_QTY' | translate }}</span></th>
                  <th>{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_HEIGHT' | translate }}</th>
                  <th>{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_WIDTH' | translate }}</th>
                  <th>{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_LENGTH' | translate }}</th>
                  <th>{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_CUBAGE' | translate }}</th>
                  <th>{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_WEIGHT' | translate }}</th>
                  <th><span class="form-label required">{{ 'TABLE_HEADERS.UNIT_PRICE' | translate }}</span></th>
                  <th><span class="form-label required">{{ 'TABLE_HEADERS.UNIT_COST' | translate }}</span></th>
                  <th><span class="form-label required">{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SALES_CURRENCY' | translate }}</span></th>
                  <th>{{ 'TABLE_HEADERS.PRICE_INCLUDES_TAX' | translate }}</th>
                  @if (isEditMode) {
                    <th>{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_AND_PRICES.ACTIONS' | translate }}</th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (row of rows; track row.unitOfMeasureId; let index = $index) {
                  <tr [formGroupName]="index">
                    <td>
                      <div class="inventory-item-uom-and-prices__unit-cell">
                        <div>
                          <p class="inventory-item-uom-and-prices__unit-name">{{ row.displayName || row.code }}</p>
                          @if (row.code) {
                            <p class="inventory-item-uom-and-prices__unit-code">{{ row.code }}</p>
                          }
                        </div>
                        @if (row.isBase) {
                          <span class="inventory-item-uom-and-prices__badge">
                            {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_AND_PRICES.BASE_BADGE' | translate }}
                          </span>
                        }
                      </div>
                    </td>
                    <td>
                      @if (isEditMode) {
                        <input
                          class="form-input form-input--compact"
                          type="number"
                          min="0.0001"
                          step="0.0001"
                          formControlName="qtyPerUnitOfMeasure"
                          [class.is-invalid]="formInteracted && form.controls.rows.controls[index].controls.qtyPerUnitOfMeasure.invalid"
                          [readOnly]="row.isBase"
                        />
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">{{ row.qtyPerUnitOfMeasure }}</span>
                      }
                    </td>
                    <td>
                      @if (isEditMode) {
                        <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="height" />
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">{{ row.height }}</span>
                      }
                    </td>
                    <td>
                      @if (isEditMode) {
                        <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="width" />
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">{{ row.width }}</span>
                      }
                    </td>
                    <td>
                      @if (isEditMode) {
                        <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="length" />
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">{{ row.length }}</span>
                      }
                    </td>
                    <td>
                      @if (isEditMode) {
                        <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="cubage" />
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">{{ row.cubage }}</span>
                      }
                    </td>
                    <td>
                      @if (isEditMode) {
                        <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="weight" />
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">{{ row.weight }}</span>
                      }
                    </td>
                    <td>
                      @if (isEditMode) {
                        <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="unitPrice" [class.is-invalid]="formInteracted && form.controls.rows.controls[index].controls.unitPrice.invalid" />
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">{{ row.unitPrice }}</span>
                      }
                    </td>
                    <td>
                      @if (isEditMode) {
                        <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="unitCost" [class.is-invalid]="formInteracted && form.controls.rows.controls[index].controls.unitCost.invalid" />
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">{{ row.unitCost }}</span>
                      }
                    </td>
                    <td>
                      @if (isEditMode) {
                        <select
                          class="form-input form-input--compact"
                          formControlName="currencyCode"
                          [class.is-invalid]="formInteracted && form.controls.rows.controls[index].controls.currencyCode.invalid"
                        >
                          @for (currencyOption of currencyOptions; track currencyOption.value) {
                            <option [value]="currencyOption.value">
                              {{ currencyOption.key | translate }}
                            </option>
                          }
                        </select>
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">
                          @if (row.currencyLabelKey) {
                            {{ row.currencyLabelKey | translate }}
                          }
                        </span>
                      }
                    </td>
                    <td>
                      @if (isEditMode) {
                        <label class="inventory-item-uom-and-prices__checkbox">
                          <input type="checkbox" formControlName="priceIncludesTax" />
                          <span>{{ row.priceIncludesTaxLabelKey | translate }}</span>
                        </label>
                      } @else {
                        <span class="inventory-item-uom-and-prices__value">{{ row.priceIncludesTaxLabelKey | translate }}</span>
                      }
                    </td>
                    @if (isEditMode) {
                      <td>
                        <button
                          type="button"
                          class="form-secondary-btn form-secondary-btn--small inventory-item-uom-and-prices__action-button inventory-item-uom-and-prices__danger-action"
                          [disabled]="!row.canRemove"
                          (click)="onRemoveUnit(row.unitOfMeasureId)"
                        >
                          <svg class="inventory-item-uom-and-prices__action-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                            <path d="M3.333 4h9.334M6.333 7v4M9.667 7v4M4.667 4l.666 8h5.334l.666-8M6.667 4V2.667h2.666V4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>
                          {{ 'BUTTONS.REMOVE' | translate }}
                        </button>
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
        <div class="inventory-item-uom-and-prices__mobile-list" formArrayName="rows">
          @for (row of rows; track row.unitOfMeasureId; let index = $index) {
            <article class="inventory-item-uom-and-prices__mobile-card" [formGroupName]="index">
              <div class="inventory-item-uom-and-prices__mobile-card-header">
                <div class="inventory-item-uom-and-prices__unit-cell">
                  <div>
                    <p class="inventory-item-uom-and-prices__unit-name">{{ row.displayName || row.code }}</p>
                    @if (row.code) {
                      <p class="inventory-item-uom-and-prices__unit-code">{{ row.code }}</p>
                    }
                  </div>
                  @if (row.isBase) {
                    <span class="inventory-item-uom-and-prices__badge">
                      {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_AND_PRICES.BASE_BADGE' | translate }}
                    </span>
                  }
                </div>
              </div>

              <div class="inventory-item-uom-and-prices__mobile-card-grid">
                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label form-label required">{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_QTY' | translate }}</span>
                  @if (isEditMode) {
                    <input
                      class="form-input form-input--compact"
                      type="number"
                      min="0.0001"
                      step="0.0001"
                      formControlName="qtyPerUnitOfMeasure"
                      [class.is-invalid]="formInteracted && form.controls.rows.controls[index].controls.qtyPerUnitOfMeasure.invalid"
                      [readOnly]="row.isBase"
                    />
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">{{ row.qtyPerUnitOfMeasure }}</span>
                  }
                </div>

                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label">{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_HEIGHT' | translate }}</span>
                  @if (isEditMode) {
                    <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="height" />
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">{{ row.height ?? '' }}</span>
                  }
                </div>

                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label">{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_WIDTH' | translate }}</span>
                  @if (isEditMode) {
                    <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="width" />
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">{{ row.width ?? '' }}</span>
                  }
                </div>

                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label">{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_LENGTH' | translate }}</span>
                  @if (isEditMode) {
                    <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="length" />
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">{{ row.length ?? '' }}</span>
                  }
                </div>

                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label">{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_CUBAGE' | translate }}</span>
                  @if (isEditMode) {
                    <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="cubage" />
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">{{ row.cubage ?? '' }}</span>
                  }
                </div>

                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label">{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.UOM_WEIGHT' | translate }}</span>
                  @if (isEditMode) {
                    <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="weight" />
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">{{ row.weight ?? '' }}</span>
                  }
                </div>

                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label form-label required">{{ 'TABLE_HEADERS.UNIT_PRICE' | translate }}</span>
                  @if (isEditMode) {
                    <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="unitPrice" [class.is-invalid]="formInteracted && form.controls.rows.controls[index].controls.unitPrice.invalid" />
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">{{ row.unitPrice ?? '' }}</span>
                  }
                </div>

                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label form-label required">{{ 'TABLE_HEADERS.UNIT_COST' | translate }}</span>
                  @if (isEditMode) {
                    <input class="form-input form-input--compact" type="number" min="0" step="0.0001" formControlName="unitCost" [class.is-invalid]="formInteracted && form.controls.rows.controls[index].controls.unitCost.invalid" />
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">{{ row.unitCost ?? '' }}</span>
                  }
                </div>

                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label form-label required">{{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.SALES_CURRENCY' | translate }}</span>
                  @if (isEditMode) {
                    <select
                      class="form-input form-input--compact"
                      formControlName="currencyCode"
                      [class.is-invalid]="formInteracted && form.controls.rows.controls[index].controls.currencyCode.invalid"
                    >
                      @for (currencyOption of currencyOptions; track currencyOption.value) {
                        <option [value]="currencyOption.value">
                          {{ currencyOption.key | translate }}
                        </option>
                      }
                    </select>
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">
                      @if (row.currencyLabelKey) {
                        {{ row.currencyLabelKey | translate }}
                      }
                    </span>
                  }
                </div>

                <div class="inventory-item-uom-and-prices__mobile-field">
                  <span class="inventory-item-uom-and-prices__mobile-label">{{ 'TABLE_HEADERS.PRICE_INCLUDES_TAX' | translate }}</span>
                  @if (isEditMode) {
                    <label class="inventory-item-uom-and-prices__checkbox">
                      <input type="checkbox" formControlName="priceIncludesTax" />
                      <span>{{ row.priceIncludesTaxLabelKey | translate }}</span>
                    </label>
                  } @else {
                    <span class="inventory-item-uom-and-prices__value">{{ row.priceIncludesTaxLabelKey | translate }}</span>
                  }
                </div>
              </div>

              @if (isEditMode) {
                <div class="inventory-item-uom-and-prices__mobile-card-actions">
                  <button
                    type="button"
                    class="form-secondary-btn form-secondary-btn--small inventory-item-uom-and-prices__action-button inventory-item-uom-and-prices__danger-action"
                    [disabled]="!row.canRemove"
                    (click)="onRemoveUnit(row.unitOfMeasureId)"
                  >
                    <svg class="inventory-item-uom-and-prices__action-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M3.333 4h9.334M6.333 7v4M9.667 7v4M4.667 4l.666 8h5.334l.666-8M6.667 4V2.667h2.666V4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    {{ 'BUTTONS.REMOVE' | translate }}
                  </button>
                </div>
              }
            </article>
          }
        </div>
      } @else {
        <p class="inventory-item-tab__empty">
          {{ 'MESSAGES.EMPTY_LIST' | translate: { param: ('COMPONENTS.INVENTORY_ITEM_OVERVIEW.TABS.UOM_AND_PRICES' | translate) } }}
        </p>
      }
    }
  </form>
}
